import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { DataStorageEnums } from 'dashboard-services/api/dataStorage';
import Query from 'dashboard-services/api/Query';

import getApiConfig from 'api/ApiConfig';
import { filesDatalakeQueries } from 'queries/datalake';
import properties from 'resources/constants/properties.json';
import { LocalStorageUtils } from 'utils';

import suggestedSymbols from './SuggestedSymbols.json';

export default (() => {
  const useDefaultFilters = () => Promise.resolve().then(() => 
    [
      {
          label: "Product",
          filterKey: "Product",
          type: DataStorageEnums.KEY_TYPE.METADATA,
      },
      {
          label: "Source",
          filterKey: "Source",
          type: DataStorageEnums.KEY_TYPE.METADATA,
      },
      {
          label: "Region",
          filterKey: "SourceRegion",
          type: DataStorageEnums.KEY_TYPE.METADATA,
      }
  ])

  const useEnvFilters = () => { 
    const dispatch = useDispatch(),
          getQueryBuilder = useCallback(() => new Query.Builder(dispatch(getApiConfig()))
            .withShouldDisplayError(false)
          , [dispatch])
    return useReturnConfigFileContent({
            getQueryBuilder,
            fileName: "defaultFilters.json",
            getDefaultFile: useDefaultFilters,
          })
  }

  const useDefaultSuggested = () => Promise.resolve().then(() => suggestedSymbols.sort((a, b) => a.symbols.Symbol.localeCompare(b.symbols.Symbol)))

  const useEnvSuggested = () => {
    const dispatch = useDispatch(),
          getQueryBuilder = useCallback(() => new Query.Builder(dispatch(getApiConfig()))
            .withShouldDisplayError(false)
          , [dispatch])
    return useReturnConfigFileContent({
            getQueryBuilder,
            fileName: "suggestedSymbols.json",
            getDefaultFile: useDefaultSuggested
          })
        }

  const useReturnConfigFileContent = ({ getDefaultFile, getQueryBuilder, fileName }) => {
    const signals = useRef([]),
          getQueryBuilderLocal = useCallback(() => {
            const controller = new AbortController()
            signals.current.push(controller)
            return getQueryBuilder()
              .withSignal(controller.signal)
          }, [getQueryBuilder]),
          getQueryBuilderNoParsing = useCallback(() => 
            getQueryBuilderLocal()
              .withOnParse(response => Promise.resolve(response))
              .withShouldTimeOut(false)
              .withShouldDisplayError(false)
          , [getQueryBuilderLocal]),
          query = useMemo(() => `groupName=${properties.baseGroup}&name=${fileName}`, [fileName]),
          { mutateAsync: searchFilesCall } = filesDatalakeQueries.useSearchFilesMutation({ getQueryBuilder: getQueryBuilderLocal, query, size: 1 }),
          { mutateAsync: getFileDefinitionCall } = filesDatalakeQueries.useGetFileDefinitionMutation({ getQueryBuilder: getQueryBuilderNoParsing }),
          { mutateAsync: getFileContentCall } = filesDatalakeQueries.useGetFileContentMutation({ getQueryBuilder: getQueryBuilderNoParsing }),
          getFile = useCallback(() => {
            if((!fileName)) {
              console.info("No config file found. Using default one.")
              return getDefaultFile();
            }
            return (
              searchFilesCall()
                .then(response => response.items?.[0]?.fid)
            )
              .then(id => {
                if(!id) {
                  console.info("No config file found. Using default one.")
                  return getDefaultFile();
                }
                return getFileDefinitionCall({ id })
                  .then(response => {
                    const location = response?.headers?.get("Location")
                    return getFileContentCall({ url: location }).then(response => response.json())
                  })
              })
          }, [fileName, getDefaultFile, getFileContentCall, getFileDefinitionCall, searchFilesCall])

    useEffect(() => () => {
      signals?.current?.forEach(s => s?.abort())
    }, [])
    return getFile;
  }

  const useUserFilters = () => {
    const localStorageUserFilters = useMemo(() => LocalStorageUtils.getSavedSeriesFilters() || [], []),
          getFilters = useEnvFilters()
    return useMemo(() => localStorageUserFilters.length > 0 ? () => Promise.resolve().then(() => localStorageUserFilters) : getFilters, [getFilters, localStorageUserFilters])
  }

  return {
    useEnvFilters,
    useEnvSuggested,
    useReturnConfigFileContent,
    useUserFilters
  }
})()